import { Store } from '@ngrx/store';
import { Update } from '@ngrx/entity';
import { inject, Injectable } from '@angular/core';

import {
  CART_ACTIONS,
  CART_STORE_ACTIVE_ACTIONS,
  CART_STORE_COMPONENT_ACTIONS,
  CART_STORE_HYDRATION_ACTIONS,
  CART_STORE_LOCAL_ACTIONS,
} from '@store/cart/cart.action';
import { CartState } from '@store/cart/cart.state';

import {
  CartItemWithOrigin,
  DoodCartModel,
  NOT_HANDLED_PRODUCT_ORIGIN,
} from '@core/models/cart.model';
import { ICartItem } from '@core/models/cart.model';
import { CartStoreSelector } from '@common/selectors/cart.selector';
import { ShopStoreSelector } from '@common/selectors/shop.selector';
import { MarketplaceStoreSelector } from '@common/selectors/marketplace.selector';
import { CartUtils } from '@app/utilities/cart.util';
import { ProductOrigin, ProductOriginWithAddedValue } from '@core/models/product-origin.model';
import { DoodProductModel } from '@core/models/product.model';
import { DoodShopModel } from '@core/models/shop.model';

@Injectable({ providedIn: 'root' })
export class CartStoreDispatcher {
  private readonly cartStoreSelector = inject(CartStoreSelector);
  private readonly shopStoreSelector = inject(ShopStoreSelector);
  private readonly marketplaceStoreSelector = inject(MarketplaceStoreSelector);
  private readonly store = inject(Store);

  // One
  addOne(entity: DoodCartModel): void {
    this.store.dispatch(CART_STORE_ACTIVE_ACTIONS.addOne({ entity }));
  }

  removeOne(id: string): void {
    this.store.dispatch(CART_STORE_ACTIVE_ACTIONS.removeOne({ id }));
  }

  // Store
  updateStore(changes: Partial<CartState>): void {
    this.store.dispatch(CART_STORE_COMPONENT_ACTIONS.updateState({ changes }));
  }

  // Active
  setActive(id: string | null): void {
    this.store.dispatch(CART_STORE_ACTIVE_ACTIONS.setActive({ active: id }));
  }

  insertActive(entity: DoodCartModel): void {
    this.store.dispatch(CART_STORE_ACTIVE_ACTIONS.insertActive({ entity }));
  }

  updateActive(changes: Partial<DoodCartModel>): void {
    this.store.dispatch(CART_STORE_ACTIVE_ACTIONS.updateActive({ changes }));
  }

  upsertActive(changes: Update<DoodCartModel>): void {
    this.store.dispatch(CART_STORE_ACTIVE_ACTIONS.upsertActive({ changes }));
  }

  resetActive(): void {
    this.store.dispatch(CART_STORE_ACTIVE_ACTIONS.resetActive());
  }

  // Last update

  checkLastCartUpdate(): void {
    this.store.dispatch(CART_STORE_COMPONENT_ACTIONS.checkLastCartUpdate());
  }

  reset(): void {
    this.store.dispatch(CART_STORE_LOCAL_ACTIONS.reset());
  }

  clear(): void {
    this.store.dispatch(CART_STORE_HYDRATION_ACTIONS.clear());
  }

  resetAndClear(): void {
    this.store.dispatch(CART_STORE_COMPONENT_ACTIONS.resetAndClear());
  }

  updateProductInCart(data: {
    productId: string;
    productShopId: string;
    quantity: number;
    itemId: string | number | undefined;
    cartItem: ICartItem;
    /** When set (e.g. BOGOF same product), use this origin instead of calculating (e.g. free item). */
    forceOrigin?: ProductOriginWithAddedValue;
  }): void {
    const cart = this.cartStoreSelector.selectActive();
    const marketplace = this.marketplaceStoreSelector.marketplace;
    const cartId = this.cartStoreSelector.state.active;

    if (!cart || !marketplace || cartId == null) {
      return;
    }

    const shop = this.resolveShopForCartItem(data.productShopId);
    const existingProductInCart = cart.products.find(cartItem => cartItem.id === data.itemId);

    // New lines need shop catalog data; existing lines can be updated from cart page without active shop.
    if (!shop && !existingProductInCart) {
      return;
    }

    const product = shop?.products.find(p => p.id === data.productId);
    const action = this.getCartAction(existingProductInCart, data);
    const isNewItem = !existingProductInCart;

    const origin =
      data.forceOrigin !== undefined
        ? data.forceOrigin
        : existingProductInCart?.origin
          ? existingProductInCart.origin
          : shop && product
            ? calculateProductOriginAndAddedValue(
                existingProductInCart,
                this.cartStoreSelector.selectLast2SelectedProductOrigin(),
                product,
                shop.products,
                data.cartItem,
              )
            : NOT_HANDLED_PRODUCT_ORIGIN;

    this.store.dispatch(
      CART_ACTIONS.updateProductInCart({
        productId: data.productId,
        productShopId: data.productShopId,
        quantity: data.quantity,
        name: product?.name ?? data.productId,
        action,
        cartItem: {
          ...data.cartItem,
          origin,
        },
        isNewItem,
        cartId,
        marketplaceId: marketplace.id,
        lastEditedCartItem: {
          ...data.cartItem,
          origin,
        },
      }),
    );
  }

  private resolveShopForCartItem(productShopId: string): DoodShopModel | null {
    const activeShop = this.shopStoreSelector.active;
    if (activeShop?.id === productShopId) {
      return activeShop;
    }

    return this.shopStoreSelector.shops.find(shop => shop.id === productShopId) ?? null;
  }

  /**
   * Determines the cart action based on the current product state and desired quantity.
   * Extracted for better readability and testability.
   */
  private getCartAction(
    existingProduct: any,
    data: { quantity: number },
  ): 'add' | 'remove' | 'increase' | 'decrease' {
    if (data.quantity === 0) {
      return 'remove';
    }
    if (!existingProduct) {
      return 'add';
    }
    return (existingProduct?.quantity ?? 0) < data.quantity ? 'increase' : 'decrease';
  }
}

export function calculateProductOriginAndAddedValue(
  existingProductInCart: CartItemWithOrigin | undefined,
  last2StoredProductOrigins: [ProductOrigin | null, ProductOrigin | null],
  product: DoodProductModel,
  products: DoodProductModel[],
  cartItem?: ICartItem,
): ProductOriginWithAddedValue {
  if (existingProductInCart?.origin) {
    return existingProductInCart.origin;
  }
  if (!last2StoredProductOrigins[0]) {
    return NOT_HANDLED_PRODUCT_ORIGIN;
  }
  const productPriceCents =
    cartItem != null
      ? CartUtils.getLineTotalPriceCents({ product, cartItem, products })
      : product.final_price;

  const lastStoredProductOrigin = last2StoredProductOrigins[0];
  switch (lastStoredProductOrigin.channel) {
    case 'up-sell':
      const triggerProductId = lastStoredProductOrigin.trigger_product_id;
      const penultimateStoredProductOrigin = last2StoredProductOrigins[1];
      if (triggerProductId === product.id) {
        return calculateProductOriginAndAddedValue(
          existingProductInCart,
          [penultimateStoredProductOrigin, null],
          product,
          products,
          cartItem,
        );
      }
      const triggerProduct = products.find(
        p => p.id === lastStoredProductOrigin.trigger_product_id,
      );

      return {
        ...last2StoredProductOrigins[0],
        added_value_cents: triggerProduct ? productPriceCents - triggerProduct.final_price : 0,
        metadata: {},
      } as ProductOriginWithAddedValue;
    case 'cross-sell':
      return {
        ...lastStoredProductOrigin,
        added_value_cents: productPriceCents,
        metadata: {},
      };

    case 'loyalty':
      return { ...lastStoredProductOrigin, added_value_cents: 0, metadata: {} }; // OK
    case 'catalog':
      return { ...lastStoredProductOrigin, added_value_cents: 0, metadata: {} }; // ok
    case 'suggestion':
    case 'suggestion_cart':
      return {
        ...lastStoredProductOrigin,
        added_value_cents: productPriceCents,
        metadata: {},
      };
    case 'bogof':
      return { ...lastStoredProductOrigin, added_value_cents: 0, metadata: {} };
    case 'promotion':
      return { ...lastStoredProductOrigin, added_value_cents: 0, metadata: {} };
    case 'unknown':
      return NOT_HANDLED_PRODUCT_ORIGIN;
    default:
      exhaustivelyExhaustiveProductOrigin(lastStoredProductOrigin);
  }
}

function exhaustivelyExhaustiveProductOrigin(origin: never): never {
  throw new Error(`Unknown product origin: ${JSON.stringify(origin)}`);
}
